import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Globe, Search, Loader2, Check, MapPin, Hash, Image as ImageIcon, Trophy } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { apiVenues } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const BusinessSignup = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState(null); // 'auto' | 'manual' | null
  const [url, setUrl] = useState("");
  const [abn, setAbn] = useState("");
  const [scraping, setScraping] = useState(false);
  const [draft, setDraft] = useState(null); // editable suggestion
  const [submitting, setSubmitting] = useState(false);

  // Manual mode fields
  const [m, setM] = useState({ name: "", category: "", address: "", image: "" });

  const requireAuth = () => {
    if (!user) {
      toast.error("Sign in as a venue partner to continue");
      nav("/login");
      return false;
    }
    return true;
  };

  const handleAutoFill = async () => {
    if (!url.trim() && !abn.trim()) {
      toast.error("Paste a website URL, an ABN, or both");
      return;
    }
    if (!requireAuth()) return;
    setScraping(true);
    try {
      const res = await apiVenues.scrape({
        url: url.trim() || undefined,
        abn: abn.trim() || undefined,
      });
      const s = res.suggestion || {};
      setDraft({
        name: s.name || "",
        category: s.category || "",
        address: s.address || "",
        image: s.image || "",
        description: s.description || "",
        abn: s.abn || "",
        entity_type: s.entity_type || "",
        abn_status: s.abn_status || "",
        source_url: s.source_url || url.trim(),
      });
      toast.success("Pulled your venue details — edit anything before publishing");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch — try a different URL");
    } finally {
      setScraping(false);
    }
  };

  const handleSubmit = async (fromDraft = true) => {
    if (!requireAuth()) return;
    const body = fromDraft
      ? {
          name: draft.name,
          category: draft.category || "venue",
          address: draft.address || "",
          image: draft.image || "",
        }
      : { name: m.name, category: m.category || "venue", address: m.address || "", image: m.image || "" };
    if (!body.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      const created = await apiVenues.create(body);
      toast.success("Venue submitted — you're live");
      nav(`/venues/${created.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't publish");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="business-signup-page">
      <header className="border-b-2 border-[#0A0A0A] bg-white">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="business-signup-back">
            <ArrowLeft className="w-4 h-4" />
            <Logo size={32} />
          </Link>
          <span className="brutal-border bg-[#FFCC00] rounded-full px-3 py-1 text-xs font-bold uppercase">
            For Venues
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <span className="label-mono text-[#555555]">Step 1 of 2</span>
        <h1 className="font-heading font-black text-4xl tracking-tight mt-1 mb-2">List your venue</h1>
        <p className="text-[#555555] mb-8 max-w-xl">
          Two ways to get on Grabb. Auto-fill is fastest — paste your website URL or ABN
          and we&apos;ll pull what we can. Edit anything before publishing.
        </p>

        {/* Mode picker */}
        {!mode && (
          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={() => setMode("auto")}
              className="brutal-card p-6 text-left bg-[#FFFBEB]"
              data-testid="business-mode-auto"
            >
              <div className="w-12 h-12 brutal-border bg-white rounded-xl flex items-center justify-center mb-4">
                <Globe className="w-6 h-6" strokeWidth={2.5} />
              </div>
              <h3 className="font-heading font-black text-xl mb-1">Auto-fill from URL or ABN</h3>
              <p className="text-sm text-[#555555] mb-3">
                Paste your website (we read OpenGraph + schema.org) and/or your 11-digit ABN.
                We&apos;ll grab the name, address, photo and category for you.
              </p>
              <span className="label-mono text-[#FF6A2C]">Recommended →</span>
            </button>

            <button
              onClick={() => setMode("manual")}
              className="brutal-card p-6 text-left"
              data-testid="business-mode-manual"
            >
              <div className="w-12 h-12 brutal-border bg-[#F8F9FA] rounded-xl flex items-center justify-center mb-4">
                <Search className="w-6 h-6" strokeWidth={2.5} />
              </div>
              <h3 className="font-heading font-black text-xl mb-1">Set up manually</h3>
              <p className="text-sm text-[#555555] mb-3">
                Type in your details. Best if you want full control or your business isn&apos;t listed online yet.
              </p>
              <span className="label-mono text-[#555555]">Manual →</span>
            </button>
          </div>
        )}

        {/* Auto-fill input */}
        {mode === "auto" && !draft && (
          <div className="brutal-card p-6 max-w-xl">
            <button onClick={() => setMode(null)} className="text-xs text-[#555555] mb-3 underline" data-testid="business-back-modes">← Change method</button>
            <label className="label-mono block mb-2">Website URL</label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="burgerurge.com.au"
              className="brutal-input"
              data-testid="business-signup-url"
            />
            <label className="label-mono block mb-2 mt-4">ABN (11 digits, optional)</label>
            <input
              value={abn}
              onChange={(e) => setAbn(e.target.value.replace(/\s+/g, ""))}
              placeholder="53121088607"
              className="brutal-input"
              maxLength={11}
              data-testid="business-signup-abn"
            />
            <button
              onClick={handleAutoFill}
              disabled={scraping}
              className="brutal-btn bg-[#FF6A2C] text-white px-4 py-3 mt-4 w-full flex items-center justify-center gap-2"
              data-testid="business-signup-fetch"
            >
              {scraping ? <Loader2 className="w-4 h-4 animate-spin" /> : "Fetch details"}
            </button>
            <p className="text-xs text-[#555555] mt-3">
              We use OpenGraph + schema.org for URLs and the ABR for ABNs. You confirm before anything goes live.
            </p>
          </div>
        )}

        {/* Auto-fill result preview */}
        {mode === "auto" && draft && (
          <div className="brutal-card overflow-hidden max-w-xl">
            {draft.image ? (
              <img src={draft.image} alt={draft.name} className="w-full h-48 object-cover border-b-2 border-[#0A0A0A]" />
            ) : (
              <div className="w-full h-48 bg-[#F8F9FA] border-b-2 border-[#0A0A0A] flex items-center justify-center">
                <ImageIcon className="w-8 h-8 text-[#999]" />
              </div>
            )}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Check className="w-4 h-4 text-[#34C759]" strokeWidth={3} />
                <span className="label-mono text-[#34C759]">Pulled — edit anything below</span>
              </div>
              <EditField label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="autofill-name" />
              <EditField label="Category" value={draft.category} onChange={(v) => setDraft({ ...draft, category: v })} testid="autofill-category" />
              <EditField label="Address" value={draft.address} onChange={(v) => setDraft({ ...draft, address: v })} testid="autofill-address" />
              <EditField label="Photo URL" value={draft.image} onChange={(v) => setDraft({ ...draft, image: v })} testid="autofill-image" />
              {draft.abn && (
                <div className="flex items-start gap-3 border-b border-[#E0E0E0] py-2">
                  <Hash className="w-4 h-4 mt-0.5 text-[#555555] shrink-0" />
                  <div className="flex-1">
                    <div className="label-mono text-[#555555]">ABN</div>
                    <div className="font-bold text-sm" data-testid="autofill-abn">{draft.abn}{draft.entity_type ? ` — ${draft.entity_type}` : ""}{draft.abn_status ? ` (${draft.abn_status})` : ""}</div>
                  </div>
                </div>
              )}
              {draft.source_url && (
                <div className="flex items-start gap-3 py-2">
                  <Globe className="w-4 h-4 mt-0.5 text-[#555555] shrink-0" />
                  <div className="flex-1">
                    <div className="label-mono text-[#555555]">Source</div>
                    <div className="font-mono text-xs text-[#555555] break-all" data-testid="autofill-source">{draft.source_url}</div>
                  </div>
                </div>
              )}
              <div className="flex gap-2 mt-5">
                <button onClick={() => setDraft(null)} className="brutal-btn bg-white py-2.5 px-4 text-sm" data-testid="autofill-discard">
                  Start over
                </button>
                <button
                  onClick={() => handleSubmit(true)}
                  disabled={submitting}
                  className="flex-1 brutal-btn bg-[#FF6A2C] text-white py-2.5"
                  data-testid="autofill-confirm"
                >
                  {submitting ? "Publishing…" : "Looks good, publish"}
                </button>
              </div>

              {/* Local Legend preview — sell the loyalty value before they publish */}
              <div className="mt-5 brutal-border rounded-lg p-4 bg-[#FFFBEB]" data-testid="legend-preview">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-4 h-4 text-[#FFCC00]" strokeWidth={2.5} />
                  <span className="label-mono">Once you&apos;re live</span>
                </div>
                <div className="font-heading font-bold text-sm mb-3">Your regulars get a Local Legend badge after 5 check-ins:</div>
                <div className="brutal-border rounded-lg p-3 bg-white flex items-center justify-between">
                  <div>
                    <div className="font-heading font-bold text-sm">{draft.name || "Your venue"}</div>
                    <div className="text-[10px] text-[#555555] font-mono">#1 REGULAR · 5+ CHECK-INS</div>
                  </div>
                  <Trophy className="w-7 h-7 text-[#FFCC00]" strokeWidth={2.5} />
                </div>
                <p className="text-xs text-[#555555] mt-3">
                  Locals show this badge in-app — they invite mates here, earn token rewards, and you keep the foot traffic.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Manual flow */}
        {mode === "manual" && (
          <div className="brutal-card p-6 max-w-xl space-y-3">
            <button onClick={() => setMode(null)} className="text-xs text-[#555555] mb-1 underline">← Change method</button>
            <ManualField label="Venue name" value={m.name} onChange={(v) => setM({ ...m, name: v })} testid="manual-name" />
            <ManualField label="Category" value={m.category} onChange={(v) => setM({ ...m, category: v })} placeholder="Cafe / Restaurant / Gym / Bar..." testid="manual-category" />
            <ManualField label="Address" value={m.address} onChange={(v) => setM({ ...m, address: v })} testid="manual-address" />
            <ManualField label="Photo URL" value={m.image} onChange={(v) => setM({ ...m, image: v })} placeholder="https://..." testid="manual-image" />
            <button onClick={() => handleSubmit(false)} disabled={submitting} className="w-full brutal-btn bg-[#FF6A2C] text-white py-3 mt-2" data-testid="manual-submit">
              {submitting ? "Publishing…" : "Publish venue"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

const EditField = ({ label, value, onChange, testid }) => (
  <div className="border-b border-[#E0E0E0] py-2 last:border-0">
    <div className="flex items-start gap-3">
      <MapPin className="w-4 h-4 mt-2 text-[#555555] shrink-0" />
      <div className="flex-1">
        <div className="label-mono text-[#555555] mb-1">{label}</div>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="brutal-input text-sm py-1" data-testid={testid} />
      </div>
    </div>
  </div>
);

const ManualField = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <label className="label-mono block mb-1.5">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="brutal-input" data-testid={testid} />
  </div>
);

export default BusinessSignup;
